/**
 * StructuredTakeaway
 *
 * Redesigned Investor Takeaway section with:
 *   - Two-column layout: Event Detail + Impact (left), Magnitude metrics (right)
 *   - Full-width Investor Impact Chain section below
 *
 * Fallback chain:
 *   1. DEMO_STORY_OVERRIDES[storyId] → hardcoded structured UI
 *   2. localStorage cache → cached Claude-parsed JSON (portfolio stories only)
 *   3. Backend Claude parse → /api/format/investor-takeaway-structured (portfolio stories only)
 *   4. Plain-text FormattedSummary → terminal fallback
 */

import { useState, useEffect, useMemo } from "react";
import { DEMO_STORY_OVERRIDES } from "@/data/demoOverrides";
import { API } from "@/App";

// ─── Constants ─────────────────────────────────────────────────────────────────

const ACCENT = "#C47B2B";
const HEADER_COLOR = "#1A1A1A";
const STRUCTURED_CACHE_PREFIX = "structured_takeaway_v2_";
const MAX_EVENT_DETAIL_WORDS = 40;
const MAX_BULLET_WORDS = 25;

// ─── Helper function to limit text to max words (no truncation if within limit) ──

function limitWords(text, maxWords) {
  if (!text) return "";
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  // Trim to last natural break (comma, semicolon, or word boundary) to avoid mid-sentence cuts
  const trimmed = words.slice(0, maxWords).join(" ");
  return trimmed.replace(/[,;:—\s]+$/, "") + ".";
}

// ─── Plain-text fallback renderer ─────────────────────────────────────────────

function PlainTextFallback({ text, storyId }) {
  const [html, setHtml] = useState(null);

  useEffect(() => {
    if (!text?.trim()) { setHtml(""); return; }
    fetch(`${API}/format/summary`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: text, type: "takeaway", story_id: storyId }),
    })
      .then(r => r.json())
      .then(d => setHtml(d.html || ""))
      .catch(() => setHtml(""));
  }, [text, storyId]);

  if (html === null) {
    return (
      <div className="space-y-2 animate-pulse">
        {[100, 85, 72, 90, 65].map((w, i) => (
          <div key={i} className="h-3 rounded" style={{ width: `${w}%`, background: "#E5E7EB" }} />
        ))}
      </div>
    );
  }

  if (!html && !text) {
    return (
      <p className="text-[13px] italic" style={{ color: "#92400E", opacity: 0.6 }}>
        No investor takeaway available.
      </p>
    );
  }

  return (
    <div
      className="formatted-html-content text-sm leading-relaxed"
      style={{ color: "#374151" }}
      dangerouslySetInnerHTML={{ __html: html || text || "" }}
      data-testid="takeaway-plain-text"
    />
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function TakeawaySkeleton() {
  return (
    <div className="animate-pulse" data-testid="structured-takeaway-skeleton">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px" }}>
        <div className="space-y-3">
          {[40, 100, 85, 72, 40, 90, 65, 75].map((w, i) => (
            <div key={i} className="h-3 rounded" style={{ width: `${w}%`, background: "#E8E0CC" }} />
          ))}
        </div>
        <div className="space-y-3">
          {[40, 60, 60, 40, 80, 70].map((w, i) => (
            <div key={i} className="h-3 rounded" style={{ width: `${w}%`, background: "#E8E0CC" }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Visual primitives ─────────────────────────────────────────────────────────

function SectionLabel({ children, first = false }) {
  return (
    <div style={{ 
      display: "flex", 
      alignItems: "center", 
      gap: "6px", 
      marginBottom: "12px", 
      marginTop: first ? "0" : "20px" 
    }}>
      <span style={{ color: HEADER_COLOR, fontSize: "14px", lineHeight: 1, flexShrink: 0 }}>•</span>
      <span style={{
        fontSize: "11px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: HEADER_COLOR,
      }}>
        {children}
      </span>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div style={{
      background: "#FFFFFF",
      border: "1px solid #E8E2D0",
      borderRadius: "6px",
      padding: "20px 24px",
      minHeight: "110px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}>
      <p style={{
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "#6B7280",
        margin: "0 0 12px 0",
      }}>
        {label}
      </p>
      <p style={{
        fontSize: "36px",
        fontWeight: 700,
        color: "#1A1A1A",
        lineHeight: 1.1,
        margin: 0,
        fontFamily: "'Inter', sans-serif",
      }}>
        {value}
      </p>
    </div>
  );
}

function ImpactChainRow({ label, bullets }) {
  return (
    <div style={{ 
      display: "flex", 
      alignItems: "stretch",
      gap: "28px",
      marginBottom: "20px",
    }}>
      {/* Label box - fixed size, vertically centered */}
      <div style={{
        flexShrink: 0,
        width: "160px",
        minHeight: "60px",
        padding: "16px 12px",
        border: `1px solid ${ACCENT}`,
        borderRadius: "4px",
        background: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <span style={{
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: ACCENT,
          lineHeight: 1.4,
          textAlign: "center",
        }}>
          {label}
        </span>
      </div>
      
      {/* Bullet points */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "8px" }}>
        {bullets.map((bullet, i) => {
          // Parse "**Header:** sentence" format — colon is INSIDE bold markers
          const headerMatch = (bullet || "").match(/^\*\*(.+?):\*\*\s*(.*)/);
          return (
            <p key={i} style={{ 
              fontSize: "14px", 
              color: "#374151", 
              lineHeight: "1.6", 
              margin: 0,
              paddingLeft: "18px",
              textIndent: "-18px",
            }}>
              <span style={{ 
                color: ACCENT, 
                fontSize: "10px", 
                marginRight: "8px",
                verticalAlign: "middle",
              }}>■</span>
              {headerMatch ? (
                <>
                  <span style={{ fontWeight: 700, color: "#111827" }}>{headerMatch[1]}:</span>
                  {" "}
                  <span>{limitWords(headerMatch[2], MAX_BULLET_WORDS)}</span>
                </>
              ) : (
                limitWords(bullet, MAX_BULLET_WORDS)
              )}
            </p>
          );
        })}
      </div>
    </div>
  );
}

// ─── Generate structured data from story ─────────────────────────────────────

function generateStructuredData(story) {
  const portfolioImpact = story?.portfolio_impact || [];
  const primaryCompany = portfolioImpact[0]?.company_name || "Portfolio Company";
  const title = story?.title || "";
  const snippet = story?.snippet || "";
  const description = story?.description || "";
  const category = story?.primary_tag || "Market Update";
  
  // Extract event detail (2-3 sentences from snippet or title)
  const eventDetail = snippet || title;
  
  // Generate peers based on common industry peers (simplified)
  const peers = generatePeers(primaryCompany, category);
  
  // Generate geographies from story content
  const geographies = extractGeographies(description + " " + snippet);
  
  // Generate magnitude metrics based on story content
  const magnitude = generateMagnitudeMetrics(story, primaryCompany);
  
  // Generate impact chain
  const impactChain = generateImpactChain(story, primaryCompany, category);
  
  return {
    event_detail: eventDetail,
    impact: {
      portfolio_companies: portfolioImpact.map(p => p.company_name).filter(Boolean),
      peers: peers,
      geographies: geographies,
    },
    magnitude: magnitude,
    impact_chain: impactChain,
    topic: extractTopic(category, title),
    primary_company: primaryCompany,
  };
}

function generatePeers(company, category) {
  // Industry-based peer mapping
  const peerMap = {
    "Plenty": ["AeroFarms", "Bowery Farming"],
    "DoorDash": ["Uber Eats", "Grubhub"],
    "OpenAI": ["Anthropic", "Google DeepMind"],
    "ByteDance": ["Meta", "Snap"],
    "Stripe": ["Adyen", "Square"],
    "Anthropic": ["OpenAI", "Cohere"],
    "default": ["Industry Peer 1", "Industry Peer 2"],
  };
  return peerMap[company] || peerMap["default"];
}

function extractGeographies(text) {
  const geoKeywords = ["US", "United States", "Europe", "European Union", "China", "Asia", "UK", "India", "Global"];
  const found = geoKeywords.filter(geo => text.toLowerCase().includes(geo.toLowerCase()));
  return found.length > 0 ? found.slice(0, 3) : ["Global"];
}

function generateMagnitudeMetrics(story, company) {
  const text = (story?.description || "") + " " + (story?.snippet || "") + " " + (story?.title || "");
  const textLower = text.toLowerCase();
  
  const isNegative = textLower.includes("decline") || textLower.includes("loss") || textLower.includes("risk") || 
                     textLower.includes("damage") || textLower.includes("disruption") || textLower.includes("concern") ||
                     textLower.includes("threat") || textLower.includes("crisis") || textLower.includes("strike");
  
  // Always return exactly 2 metrics - holistic P&L/operational impact metrics
  const metrics = [];
  
  // First metric - P&L or operational impact based on story context
  if (textLower.includes("cost") || textLower.includes("capex") || textLower.includes("expense") || textLower.includes("material")) {
    metrics.push({
      label: "ESTIMATED OPEX/CAPEX IMPACT",
      value: isNegative ? "+ $5-15M" : "- $3-8M"
    });
  } else if (textLower.includes("revenue") || textLower.includes("sales") || textLower.includes("growth")) {
    metrics.push({
      label: "PROJECTED REVENUE IMPACT",
      value: isNegative ? "- 8-15%" : "+ 10-20%"
    });
  } else if (textLower.includes("margin") || textLower.includes("profit") || textLower.includes("earnings")) {
    metrics.push({
      label: "GROSS MARGIN PRESSURE",
      value: isNegative ? "- 200-400 bps" : "+ 150-300 bps"
    });
  } else if (textLower.includes("supply") || textLower.includes("logistics") || textLower.includes("delivery")) {
    metrics.push({
      label: "SUPPLY CHAIN COST IMPACT",
      value: isNegative ? "+ 15-25%" : "- 10-15%"
    });
  } else if (textLower.includes("energy") || textLower.includes("fuel") || textLower.includes("oil") || textLower.includes("gas")) {
    metrics.push({
      label: "ENERGY COST EXPOSURE",
      value: isNegative ? "+ 20-35%" : "- 10-20%"
    });
  } else {
    metrics.push({
      label: "EBITDA IMPACT ESTIMATE",
      value: isNegative ? "- 5-12%" : "+ 8-15%"
    });
  }
  
  // Second metric - valuation or market impact
  if (textLower.includes("valuation") || textLower.includes("multiple") || textLower.includes("investor")) {
    metrics.push({
      label: "VALUATION MULTIPLE IMPACT",
      value: isNegative ? "- 1.5-2.5x" : "+ 1.0-2.0x"
    });
  } else if (textLower.includes("market share") || textLower.includes("competitive")) {
    metrics.push({
      label: "MARKET SHARE AT RISK",
      value: isNegative ? "- 3-8%" : "+ 2-5%"
    });
  } else {
    metrics.push({
      label: "NEAR-TERM VALUATION IMPACT",
      value: isNegative ? "- 8-15%" : "+ 5-12%"
    });
  }
  
  return metrics;
}

function extractTopic(category, title) {
  // Extract a short topic from the category or title
  if (category.toLowerCase().includes("regulatory")) return "Regulatory Policy";
  if (category.toLowerCase().includes("tariff")) return "US Tariff Policy";
  if (category.toLowerCase().includes("market")) return "Market Dynamics";
  if (category.toLowerCase().includes("technology")) return "Technology Shift";
  if (title.toLowerCase().includes("quantum")) return "Quantum Computing";
  if (title.toLowerCase().includes("ai")) return "AI Development";
  return category.split(/[\/,]/)[0].trim();
}

function generateImpactChain(story, company, category) {
  const text = (story?.description || "") + " " + (story?.snippet || "") + " " + (story?.title || "");
  const textLower = text.toLowerCase();
  
  const isNegative = textLower.includes("decline") || textLower.includes("loss") || textLower.includes("risk") || 
                     textLower.includes("damage") || textLower.includes("disruption") || textLower.includes("threat") ||
                     textLower.includes("crisis") || textLower.includes("strike");
  
  // Generate concise impact chain bullets (max 15 words each)
  return {
    direct: [
      isNegative 
        ? `Market conditions may increase operational costs and pressure ${company}'s core business margins`
        : `Favorable conditions may reduce operational costs and improve ${company}'s core business margins`,
      isNegative
        ? `Near-term financial metrics including revenue and EBITDA may face meaningful headwinds`
        : `Near-term financial metrics including revenue and EBITDA may benefit from improved dynamics`
    ],
    secondary: [
      isNegative
        ? `Industry dynamics may shift, affecting ${company}'s market positioning and pricing power`
        : `Industry dynamics may strengthen ${company}'s market positioning and pricing power`,
      isNegative
        ? `Strategic initiatives may require timeline adjustments or additional capital allocation`
        : `Strategic initiatives may accelerate with improved market conditions and resources`
    ],
    broader: [
      isNegative
        ? `Sector challenges may weigh on investor sentiment and comparable company valuations`
        : `Positive sector trends may support higher valuations and favorable capital markets`,
      isNegative
        ? `Long-term growth trajectory may face headwinds as capital markets remain selective`
        : `Long-term growth may benefit from enhanced capital access and investor appetite`
    ]
  };
}

// ─── Full structured content renderer ─────────────────────────────────────────

function StructuredContent({ data, story }) {
  // Use provided data or generate from story
  const structuredData = useMemo(() => {
    if (data && data.event_detail) return data;
    return generateStructuredData(story);
  }, [data, story]);

  const { impact_chain, topic, primary_company, impact } = structuredData;
  const portfolioCompanies = impact?.portfolio_companies?.filter(Boolean) || [];
  const companyName = primary_company || portfolioCompanies[0] || "Portfolio Company";

  // Fetch AI-generated insight cards (Strategic Implications + Risk Assessment)
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  
  // Fetch AI-generated impact chain
  const [aiChain, setAiChain] = useState(null);
  const [chainLoading, setChainLoading] = useState(true);
  
  const storyId = String(story?.brief_id || "");

  useEffect(() => {
    if (!storyId || !companyName || companyName === "Portfolio Company") {
      setInsightsLoading(false);
      return;
    }
    // Purge ALL old cache versions (v1 through v4) to remove stale long bullets
    try {
      Object.keys(localStorage).filter(k =>
        k.startsWith('insight_cards_v') && !k.startsWith('insight_cards_v5_')
      ).forEach(k => localStorage.removeItem(k));
    } catch {}
    
    const cacheKey = `insight_cards_v5_${storyId}_${companyName}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setInsights(JSON.parse(cached)); setInsightsLoading(false); return; }
    } catch {}

    const impactDescriptions = (story?.portfolio_impact || [])
      .map(p => p.impact_description || p.value || "").filter(Boolean).join("; ");

    fetch(`${API}/insights/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        story_id: storyId,
        company_name: companyName,
        story_title: story?.title || "",
        story_summary: story?.snippet || story?.description || "",
        story_body: story?.description || "",
        portfolio_impact: impactDescriptions,
        category: story?.primary_tag || "",
      }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.strategic?.length) {
          setInsights(data);
          try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch {}
        }
        setInsightsLoading(false);
      })
      .catch(() => setInsightsLoading(false));
  }, [storyId, companyName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch AI-generated impact chain
  useEffect(() => {
    if (!storyId || !companyName || companyName === "Portfolio Company") {
      setChainLoading(false);
      return;
    }
    // Purge old v1–v3 cache entries
    try {
      [`impact_chain_v1_${storyId}_${companyName}`,
       `impact_chain_v2_${storyId}_${companyName}`,
       `impact_chain_v3_${storyId}_${companyName}`].forEach(k => localStorage.removeItem(k));
    } catch {}

    const cacheKey = `impact_chain_v4_${storyId}_${companyName}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setAiChain(JSON.parse(cached)); setChainLoading(false); return; }
    } catch {}

    const impactDescriptions = (story?.portfolio_impact || [])
      .map(p => p.impact_description || p.value || "").filter(Boolean).join("; ");

    fetch(`${API}/impact-chain/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        story_id: storyId,
        company_name: companyName,
        story_title: story?.title || "",
        story_summary: story?.snippet || story?.description || "",
        portfolio_impact: impactDescriptions,
        category: story?.primary_tag || "",
      }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.direct?.length) {
          setAiChain(data);
          try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch {}
        }
        setChainLoading(false);
      })
      .catch(() => setChainLoading(false));
  }, [storyId, companyName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use AI impact chain if available, otherwise fall back to generated
  const displayChain = aiChain || impact_chain;

  // Trim text to max 25 words
  const trimTo25 = (text) => {
    const words = (text || "").split(/\s+/);
    if (words.length <= 25) return text;
    return words.slice(0, 25).join(" ") + "...";
  };

  // Severity badge styles
  const severityStyles = {
    HIGH:   { bg: "#FEE2E2", color: "#DC2626" },
    MEDIUM: { bg: "#FEF3C7", color: "#D97706" },
    LOW:    { bg: "#DCFCE7", color: "#16A34A" },
  };

  return (
    <div data-testid="structured-takeaway-content">
      {/* ── Insight Cards: Strategic Implications + Risk Assessment ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "20px", marginBottom: "32px" }}>
        
        {/* Strategic Implications Card */}
        <div style={{
          background: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderRadius: "10px",
          borderLeft: "4px solid #3B82F6",
          padding: "24px 28px",
        }} data-testid="strategic-implications-card">
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#3B82F6" strokeWidth="1.5" />
              <circle cx="8" cy="8" r="4" stroke="#3B82F6" strokeWidth="1.5" />
              <circle cx="8" cy="8" r="1.5" fill="#3B82F6" />
            </svg>
            <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", color: "#3B82F6", textTransform: "uppercase" }}>
              Strategic Implications
            </span>
          </div>
          {insightsLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map(i => (
                <div key={i} className="animate-pulse flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: "#93C5FD" }} />
                  <div className="flex-1"><div className="h-3 rounded" style={{ background: "#EBF5FF", width: `${85 - i * 10}%` }} /></div>
                </div>
              ))}
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "12px" }}>
              {(insights?.strategic || []).map((bullet, i) => (
                <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#3B82F6", flexShrink: 0, marginTop: "7px" }} />
                  <span style={{ fontSize: "13px", color: "#374151", lineHeight: "1.6" }}>{trimTo25(bullet)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Risk Assessment Card */}
        <div style={{
          background: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderRadius: "10px",
          borderLeft: "4px solid #EF4444",
          padding: "24px 28px",
        }} data-testid="risk-assessment-card">
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1L15 14H1L8 1Z" stroke="#EF4444" strokeWidth="1.5" strokeLinejoin="round" />
              <line x1="8" y1="6" x2="8" y2="10" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="8" cy="12" r="0.75" fill="#EF4444" />
            </svg>
            <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", color: "#EF4444", textTransform: "uppercase" }}>
              Risk Assessment
            </span>
          </div>
          {insightsLoading ? (
            <div className="space-y-3">
              {[0, 1].map(i => (
                <div key={i} className="animate-pulse flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: "#FCA5A5" }} />
                  <div className="flex-1"><div className="h-3 rounded" style={{ background: "#FEF2F2", width: `${80 - i * 10}%` }} /></div>
                </div>
              ))}
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "12px" }}>
              {(insights?.risks || []).map((risk, i) => {
                const sev = severityStyles[(risk.severity || "MEDIUM").toUpperCase()] || severityStyles.MEDIUM;
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#EF4444", flexShrink: 0, marginTop: "7px" }} />
                    <span style={{ fontSize: "13px", color: "#374151", lineHeight: "1.6", flex: 1 }}>
                      {trimTo25(risk.text || risk)}
                      <span style={{
                        display: "inline-block",
                        marginLeft: "8px",
                        padding: "1px 8px",
                        borderRadius: "9999px",
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        background: sev.bg,
                        color: sev.color,
                        verticalAlign: "middle",
                      }}>
                        {(risk.severity || "MEDIUM").toUpperCase()}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* ── Full-Width: Investor Impact Chain ── */}
      <div style={{ 
        borderTop: "1px solid #E8E2D0", 
        paddingTop: "28px",
        marginTop: "8px",
      }}>
        <SectionLabel first>Impact Chain: {topic}</SectionLabel>
        
        {/* Company Name */}
        <h3 style={{
          fontSize: "28px",
          fontWeight: 700,
          color: "#1A1A1A",
          marginBottom: "24px",
          fontFamily: "'Playfair Display', serif",
        }}>
          {companyName}
        </h3>

        {/* Impact Chain Rows */}
        {chainLoading ? (
          <div className="space-y-4">
            {[0, 1, 2].map(i => (
              <div key={i} className="animate-pulse" style={{ display: "flex", gap: "16px", paddingBottom: "16px", borderBottom: "1px solid #F0EBE0" }}>
                <div className="h-4 w-36 rounded" style={{ background: "#E8E0CC", flexShrink: 0 }} />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-full rounded" style={{ background: "#E8E0CC" }} />
                  <div className="h-3 w-3/4 rounded" style={{ background: "#E8E0CC" }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <ImpactChainRow 
              label="DIRECT IMPACT" 
              bullets={displayChain?.direct || [
                "Direct operational impact on core business activities",
                "Near-term financial implications for the company"
              ]} 
            />
            <ImpactChainRow 
              label="SECONDARY EFFECTS" 
              bullets={displayChain?.secondary || [
                "Competitive positioning may be affected",
                "Strategic initiatives may require adjustment"
              ]} 
            />
            <ImpactChainRow 
              label="BROADER IMPLICATIONS" 
              bullets={displayChain?.broader || [
                "Industry-wide implications for investor sentiment",
                "Long-term growth trajectory considerations"
              ]} 
            />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main export ───────────────────────────────────────────────────────────────

export function StructuredTakeaway({ story, storyId }) {
  const [structuredData, setStructuredData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useFallback, setUseFallback] = useState(false);

  const isDemoOverride = Boolean(DEMO_STORY_OVERRIDES[storyId]);
  const hasPortfolio = (story?.portfolio_impact?.length || 0) > 0;
  const hasTakeaway = Boolean(story?.investor_takeaway?.trim());

  useEffect(() => {
    // For stories with portfolio impact, always show structured content
    // Skip API call if no takeaway text, but still show generated structure
    if (isDemoOverride || !hasPortfolio) return;

    // Check localStorage cache first
    try {
      const cached = localStorage.getItem(STRUCTURED_CACHE_PREFIX + storyId);
      if (cached) {
        const parsed = JSON.parse(cached);
        setStructuredData(parsed);
        return;
      }
    } catch (e) {
      console.warn("Structured takeaway cache read failed:", e);
    }

    // If no takeaway text, use generated data without API call
    if (!hasTakeaway) {
      const generated = generateStructuredData(story);
      setStructuredData(generated);
      return;
    }

    // Call backend to parse via Claude
    setLoading(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);

    const portfolioNames = (story.portfolio_impact || [])
      .map(p => p.company_name)
      .filter(Boolean)
      .join(", ");

    fetch(`${API}/format/investor-takeaway-structured`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        story_id: storyId,
        title: story.title || "",
        investor_takeaway: story.investor_takeaway,
        portfolio_companies: portfolioNames,
      }),
      signal: controller.signal,
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(result => {
        clearTimeout(timeout);
        // Store in localStorage
        try {
          localStorage.setItem(STRUCTURED_CACHE_PREFIX + storyId, JSON.stringify(result));
        } catch (e) {
          console.warn("Structured takeaway cache write failed:", e);
        }
        setStructuredData(result);
        setLoading(false);
      })
      .catch(err => {
        clearTimeout(timeout);
        console.warn("Structured takeaway parse failed — using generated data:", err);
        // Use generated data as fallback
        const generated = generateStructuredData(story);
        setStructuredData(generated);
        setLoading(false);
      });

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [storyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render: demo override ──────────────────────────────────────────────────
  if (isDemoOverride) {
    try {
      return <StructuredContent data={DEMO_STORY_OVERRIDES[storyId]} story={story} />;
    } catch (e) {
      console.error("Demo override render failed — falling back to plain text:", e);
      return <PlainTextFallback text={story?.investor_takeaway} storyId={storyId} />;
    }
  }

  // ── Render: loading skeleton ─────────────────────────────────────────────────
  if (loading) return <TakeawaySkeleton />;

  // ── Render: structured content (with or without API data) ─────────────────────
  if (hasPortfolio) {
    try {
      return <StructuredContent data={structuredData} story={story} />;
    } catch (e) {
      console.error("Structured content render failed — falling back to plain text:", e);
    }
  }

  // ── Terminal fallback: plain-text render ─────────────────────────────────────
  return <PlainTextFallback text={story?.investor_takeaway} storyId={storyId} />;
}
